import React from 'react';
import {
  PhoneCall,
  PhoneDisconnect,
  PhoneTransfer,
  CalendarCheck,
  CheckCircle,
  ClockCountdown,
} from '@phosphor-icons/react';

import COLORS from '../../../util/constants/colors';
import { formatDisconnectionReason } from '../disconnectionReasonLabels';

const DISCONNECTION_REASON_PRESENTATION = {
  call_transfer: {
    icon: <PhoneTransfer size={16} weight="bold" />,
    color: COLORS.ORANGE_PRIMARY_LITE,
    backgroundColor: COLORS.ORANGE_QUARTERNARY_LITE,
  },
  user_hangup: {
    icon: <PhoneDisconnect size={16} weight="bold" />,
    color: COLORS.BLUE_PRIMARY_LITE,
    backgroundColor: COLORS.LAVENDER,
  },
  agent_hangup: {
    icon: <PhoneDisconnect size={16} weight="bold" />,
    color: 'black',
    backgroundColor: COLORS.GREY,
  },
};

const DISCONNECTION_REASON_TAG_STYLE = {
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  borderRadius: 12,
  padding: '2px 8px',
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '16px',
  minHeight: 24,
  maxWidth: 180,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
};

const getDisconnectionReasonTag = (callLog) => {
  const rawLabel = formatDisconnectionReason(callLog);
  if (rawLabel === null || rawLabel === undefined || String(rawLabel).trim() === '') return null;

  const presentation = DISCONNECTION_REASON_PRESENTATION[callLog?.disconnection_reason_key];
  if (presentation) {
    return {
      label: rawLabel,
      value: callLog.disconnection_reason_key,
      ...presentation,
    };
  }

  return {
    label: String(rawLabel),
    value: 'other',
    icon: <PhoneCall size={16} weight="bold" />,
    color: COLORS.TEXT_SECONDARY_LITE,
    backgroundColor: COLORS.GREY,
  };
};

// Scheduled-via-MAIRA: only the `scheduled` outcome earns a badge — a green
// success pill rendered in the Status column INSTEAD of the disconnection pill
// (see inboundColumns). `not_scheduled` deliberately has NO badge anywhere: the
// value stays valid at ingestion, those calls just show their disconnection pill
// like any other call. Reuses DISCONNECTION_REASON_TAG_STYLE for the pill shape.
const SCHEDULED_VIA_MAIRA_TAG = {
  value: 'scheduled',
  label: 'Scheduled via MAIRA',
  icon: <CalendarCheck size={16} weight="bold" />,
  color: COLORS.SUCCESS_HOVER_LITE,
  backgroundColor: COLORS.SUCCESS_TERTIARY_LITE,
};

const getScheduledViaMairaTag = (callLog) => (
  callLog?.scheduling_outcome === 'scheduled' ? SCHEDULED_VIA_MAIRA_TAG : null
);

// ENG-3107: the inbound projection of the ENG-3061 delivery lifecycle. `state`
// is supplied by the API (`result_delivery.state`) and is already gated on the
// org's inbound_result_delivery_outcomes feature, so there is no client-side
// flag check here — an unentitled org simply never receives the key.
//
// The two states are deliberately visually distinct rather than shades of the
// same colour: "delivered" is a settled, verified fact staff can act on, while
// "pending verification" is a claim ENG-3061 has not yet confirmed from the
// recording, and treating them alike would invite the wrong follow-up.
// `description` is the slide-out's longer-form sentence. It lives here beside the
// pill so a new state cannot be added with a badge but no explanation.
const RESULT_DELIVERY_PRESENTATION = {
  delivered: {
    label: 'Results Delivered',
    description: 'Outbound results were delivered to the patient during this call.',
    icon: <CheckCircle size={16} weight="bold" />,
    color: COLORS.SUCCESS_HOVER_LITE,
    backgroundColor: COLORS.SUCCESS_TERTIARY_LITE,
  },
  unverified: {
    label: 'Delivery Pending',
    description: 'A result delivery was recorded on this call but is still awaiting verification.',
    icon: <ClockCountdown size={16} weight="bold" />,
    color: COLORS.ORANGE_PRIMARY_LITE,
    backgroundColor: COLORS.ORANGE_QUARTERNARY_LITE,
  },
};

const getResultDeliveryTag = (callLog) => {
  const state = callLog?.result_delivery?.state;
  const presentation = RESULT_DELIVERY_PRESENTATION[state];
  if (!presentation) return null;

  return { value: state, ...presentation };
};

// Shared status-pill markup for every Voice Inbox badge — the disconnection and
// Scheduled-via-MAIRA cells, and the slide-out's result-delivery pill — so they
// cannot drift independently. Lives here beside the tag builders that feed it.
// Expects a non-null tag { color, backgroundColor, icon, label }.
const renderStatusPill = (tag) => (
  <div
    className="voice-inbound-status-pill"
    style={{
      ...DISCONNECTION_REASON_TAG_STYLE,
      color: tag.color,
      border: `1px solid ${tag.color}`,
      backgroundColor: tag.backgroundColor,
    }}
  >
    <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{tag.icon}</span>
    {tag.label}
  </div>
);

// DISCONNECTION_REASON_TAG_STYLE is deliberately NOT exported: renderStatusPill is
// now its only consumer, so the pill shape stays owned by the one function that
// draws it. DISCONNECTION_REASON_PRESENTATION stays exported for the existing
// disconnection-label tests.
export {
  DISCONNECTION_REASON_PRESENTATION,
  getDisconnectionReasonTag,
  getScheduledViaMairaTag,
  getResultDeliveryTag,
  renderStatusPill,
};
